import type { KeyboardEvent } from 'react';
import type { BudgetListItem } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatMoneyDisplay } from '../../lib/formatMoneyDisplay';
import { MoneyGivenCashWatermark } from './MoneyGivenCashWatermark';

type EmployerMoneyGivenDashboardCardProps = {
  primaryBudget: BudgetListItem | undefined;
  primaryBudgetRange: string;
  submittedSettlementCount: number;
  onSettlementAlert: () => void;
};

export const EmployerMoneyGivenDashboardCard = ({
  primaryBudget,
  primaryBudgetRange,
  submittedSettlementCount,
  onSettlementAlert,
}: EmployerMoneyGivenDashboardCardProps) => {
  const { t } = useLanguage();

  const handleSettlementAlertKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSettlementAlert();
    }
  };

  return (
    <section
      className="relative overflow-hidden rounded-xl border border-gray-200/80 bg-gradient-to-br from-amber-50/50 via-white to-emerald-50/40 p-4 shadow-sm dark:border-gray-200/60 dark:bg-gradient-to-br dark:from-gray-900 dark:via-emerald-950/40 dark:to-gray-900/70"
      aria-labelledby="dash-budget-heading"
    >
      <div
        className="pointer-events-none absolute -right-10 -top-8 opacity-[0.55] dark:opacity-60"
        aria-hidden
      >
        <MoneyGivenCashWatermark className="h-[200px] w-[280px] max-w-none" />
      </div>

      <div className="relative z-10">
        <div className="mb-3 flex items-start gap-2">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </span>
          <h2
            id="dash-budget-heading"
            className="text-base font-semibold leading-tight text-gray-900 dark:text-gray-100"
          >
            {t('employerDashboard.monthExpenseTitle', { range: primaryBudgetRange })}
          </h2>
        </div>
        {!primaryBudget ? (
          <p className="text-sm text-gray-500 dark:text-gray-300">{t('employerDashboard.budgetPlaceholder')}</p>
        ) : (
          <div className="rounded-3xl border border-gray-200 bg-white/75 px-4 py-3 shadow-sm backdrop-blur-[2px] dark:border-gray-500/70 dark:bg-gray-800/55">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-2 text-[15px] font-medium text-gray-500 dark:text-gray-200">
                <svg className="h-[1.4rem] w-[1.4rem] text-gray-500 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <rect
                    x="2"
                    y="6"
                    width="20"
                    height="12"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                  <path
                    d="M22 10C21.4747 10 20.9546 9.89654 20.4693 9.69552C19.984 9.4945 19.543 9.19986 19.1716 8.82843C18.8001 8.45699 18.5055 8.01604 18.3045 7.53073C18.1035 7.04543 18 6.52529 18 6L22 6L22 10Z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                  <path
                    d="M18 18C18 16.9391 18.4214 15.9217 19.1716 15.1716C19.9217 14.4214 20.9391 14 22 14L22 18L18 18Z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                  <path
                    d="M2 14C3.06087 14 4.07828 14.4214 4.82843 15.1716C5.57857 15.9217 6 16.9391 6 18L2 18L2 14Z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                  <path
                    d="M6 6C6 7.06087 5.57857 8.07828 4.82843 8.82843C4.07828 9.57857 3.06087 10 2 10L2 6H6Z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                  <path
                    d="M14.0741 9.5H11.3333C10.597 9.5 10 10.0596 10 10.75C10 11.4404 10.597 12 11.3333 12H13.1111C13.8475 12 14.4444 12.5596 14.4444 13.25C14.4444 13.9404 13.8475 14.5 13.1111 14.5H10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                  <path
                    d="M12 9.51733V8.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                  <path
                    d="M12 15.5173V14.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                  />
                </svg>
                {t('employerDashboard.budgetTotal')}
              </span>
              <span className="text-4xl font-bold leading-none text-gray-900 dark:text-white">
                {formatMoneyDisplay(primaryBudget.amount)}
              </span>
            </div>
          </div>
        )}
        {submittedSettlementCount > 0 ? (
          <div
            role="button"
            tabIndex={0}
            onClick={onSettlementAlert}
            onKeyDown={handleSettlementAlertKeyDown}
            className="mt-4 flex cursor-pointer items-center gap-3 rounded-lg border-l-4 border-amber-500 bg-white/70 p-3 backdrop-blur-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-gray-800/50"
          >
            <span className="text-amber-600">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </span>
            <span className="flex-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('employerDashboard.settlementPendingLine', { count: submittedSettlementCount })}
            </span>
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        ) : null}
      </div>
    </section>
  );
};
